import { z } from 'zod';
import { isValidDashboardTilePositions } from '../../../../utils/dashboardTilePositions';
import { AI_DASHBOARD_LAYOUT_TEMPLATES } from '../../dashboardLayout';
import { baseOutputMetadataSchema, structuredToolOutputSchema, } from '../outputMetadata';
import { createToolSchema } from '../toolSchemaBuilder';
import { chartConfigBuiltinOnlySchema, toolRunQueryArgsSchema, toolRunQueryArgsSchemaPersisted, toolRunQueryArgsSchemaTransformed, } from './toolRunQueryArgs';
export const TOOL_DASHBOARD_V2_DESCRIPTION = `

Use this tool to generate multiple visualizations for a dashboard based on a specific theme.

Instead of making multiple parallel tool calls for individual visualizations, this tool takes an array of visualization configurations and generates them all at once, ensuring thematic consistency and better performance.

Dashboard Design Philosophy:
Create "summary" or "executive" level dashboards that provide high-level overviews of specific topics (e.g., Support Dashboard, Revenue Performance, Sales Operations). These dashboards should be designed to monitor trends and key metric performance at a glance.

Recommended Dashboard Structure:
1. **Summary Metrics at the Top**: Start with key performance indicators (KPIs) as big_number visualizations. Use several big_number visualizations for a few related headline metrics rather than a table
2. **Time-Series Analysis**: Include time-series charts (line or area charts) to show how metrics trend over time
3. **Categorical Comparisons**: Use bar or horizontal bar charts to compare metrics across categories
4. **Proportional Analysis**: Use pie charts to show part-to-whole relationships (e.g., market share, revenue by segment)
5. **Correlation Analysis**: Use scatter plots to explore relationships between two metrics
6. **Conversion Flows**: Use funnel charts to visualize sequential conversion processes (e.g., sales funnel, user onboarding)
7. **Detailed Breakdowns**: Add tables only when users need detailed rows or many line items that do not fit big_number or chart visualizations

Tool selection rule:
- If a response requires more than one chart, you MUST use this dashboard tool and include all requested charts as visualizations. Do not create multiple standalone chart responses for the same request.

Markdown tiles:
- Use markdownTiles to add report narrative below the charts: what each section shows, key findings with concrete values or directional changes, and why they matter.
- For diagnostic or explanatory requests (main driver, biggest contributor, reason behind an outcome), add a conclusion tile immediately before the Methodology tile. Its first sentence names the strongest supported driver plainly, then cites the key evidence from the charts and any real caveat. Do not put the conclusion only in the Methodology tile.
- Write each tile's content as real markdown (short paragraphs or bullets), never one long unformatted line.
- The last markdown tile should be titled "Methodology" and list, one fact per bullet, the explores, dimensions, metrics, filters, sorting/grouping, and any table calculations used. Do not invent methods that were not used.
`;
const dashboardV2MarkdownTileSchema = z.object({
    title: z.string().min(1).describe('Short title for the markdown tile'),
    content: z
        .string()
        .min(1)
        .describe('Markdown narrative for the tile. Use bullets or short paragraphs, be specific about the analysis performed and findings, and avoid generic filler text.'),
});
const markdownTilesDescription = '(Optional) Markdown tiles rendered below the charts, in order. null for a dashboard without narrative tiles.';
// Dashboard visualization schema - use generateVisualization format for each
// visualization. Custom chart types are thread-only for the PoC, so each
// dashboard visualization pins the builtin-only chart config.
const dashboardV2VisualizationSchema = toolRunQueryArgsSchema.extend({
    chartConfig: chartConfigBuiltinOnlySchema,
});
const dashboardV2Fields = {
    title: z.string().describe('A descriptive title for the dashboard'),
    description: z
        .string()
        .describe('A descriptive summary or explanation for the dashboard.'),
};
const visualizationsDescription = 'Array of visualization configurations to generate for this dashboard. Each should contribute to the overall theme and leverage the available chart types (big_number, table, bar, horizontal, line, scatter, pie, funnel, treemap, map, heatmap).';
// Advertised agent contract: formula-only table calcs per visualization.
export const toolDashboardV2ArgsSchema = createToolSchema()
    .extend({
    ...dashboardV2Fields,
    visualizations: z
        .array(dashboardV2VisualizationSchema)
        .min(2)
        .max(15)
        .describe(visualizationsDescription),
    markdownTiles: z
        .array(dashboardV2MarkdownTileSchema)
        .max(10)
        .nullish()
        .describe(markdownTilesDescription),
})
    .build();
// Wide variant for parsing persisted artifacts and incoming tool calls; it
// accepts everything the advertised schema produces plus legacy template
// table calcs. Tracks toolRunQueryArgsSchemaPersisted (see its invariant).
export const toolDashboardV2ArgsSchemaPersisted = createToolSchema()
    .extend({
    ...dashboardV2Fields,
    // A partially validated dashboard may retain one usable visualization.
    visualizations: z
        .array(toolRunQueryArgsSchemaPersisted)
        .min(1)
        .max(15)
        .describe(visualizationsDescription),
    markdownTiles: z
        .array(dashboardV2MarkdownTileSchema)
        .max(10)
        .nullish()
        .describe(markdownTilesDescription),
    // Server-owned presentation metadata; never advertised as tool input.
    layout: z
        .object({
        template: z.enum(AI_DASHBOARD_LAYOUT_TEMPLATES),
        positions: z
            .array(z.object({
            x: z.number().int().nonnegative(),
            y: z.number().int().nonnegative(),
            w: z.number().int().positive(),
            h: z.number().int().positive(),
        }))
            .min(1)
            .max(15),
    })
        .optional(),
})
    .build()
    .superRefine((value, ctx) => {
    if (value.layout &&
        !isValidDashboardTilePositions(value.layout.positions, value.visualizations.length)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['layout'],
            message: 'Dashboard layout must place every visualization within the grid without overlap.',
        });
    }
});
export const toolDashboardV2ArgsSchemaTransformed = toolDashboardV2ArgsSchemaPersisted.transform((data) => ({
    ...data,
    visualizations: data.visualizations.map((viz) => toolRunQueryArgsSchemaTransformed.parse(viz)),
}));
export const toolDashboardV2StructuredContentSchema = z.object({
    visualizationCount: z
        .number()
        .nullable()
        .describe('How many visualizations the dashboard was created with; null when the result text does not state the count.'),
    excludedVisualizations: z
        .array(z.object({
        title: z.string().describe('Title of the visualization.'),
        error: z
            .string()
            .describe('Why the visualization failed validation.'),
    }))
        .describe('Visualizations left out of the dashboard because they failed validation; empty when every visualization was included.'),
    layout: z
        .object({
        template: z.enum(AI_DASHBOARD_LAYOUT_TEMPLATES),
        positions: z.array(z.object({
            x: z.number().int(),
            y: z.number().int(),
            w: z.number().int(),
            h: z.number().int(),
        })),
    })
        .nullable(),
    defaultLayoutNote: z.string().nullable(),
});
export const toolDashboardV2OutputSchema = structuredToolOutputSchema({
    metadata: baseOutputMetadataSchema,
    structuredContent: toolDashboardV2StructuredContentSchema,
});
//# sourceMappingURL=toolDashboardV2Args.js.map